<template>
  <!-- System gate, not a user-dismissible modal: teleported to <body> and on
       the z-critical layer, above all regular app chrome and modals. -->
  <Teleport to="body">
    <div
      v-if="showModal"
      class="fixed inset-0 flex items-center justify-center z-critical p-4"
      style="background: rgba(18, 23, 26, 0.45); backdrop-filter: blur(4px);"
    >
      <div
        class="w-full max-w-md overflow-hidden"
        style="background: var(--surface); border: 1px solid var(--border-subtle); border-radius: 8px; box-shadow: var(--shadow-2xl);"
        @click.stop
      >
        <!-- Loading State -->
        <div v-if="isLoading" class="p-8 text-center">
          <div class="flex justify-center mb-4">
            <AppSpinner size="lg" />
          </div>
          <h2 class="mb-2" style="font-size: var(--text-xl); color: var(--text-primary);">
            {{ t('magic_link.verifying_title') }}
          </h2>
          <p style="color: var(--text-tertiary); font-size: var(--text-sm);">{{ t('magic_link.verifying_description') }}</p>
        </div>

        <!-- Success State -->
        <div v-else-if="result?.success" class="p-8 text-center">
          <div class="w-14 h-14 flex items-center justify-center mx-auto mb-4" style="background: var(--forest-100); border-radius: 6px;">
            <svg class="w-7 h-7 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 class="mb-2" style="font-size: var(--text-xl); color: var(--text-primary);">
            {{ t('magic_link.welcome_title', { name: result.householdName }) }}
          </h2>
          <p class="mb-6" style="color: var(--text-secondary); font-size: var(--text-sm);">
            {{ t('magic_link.welcome_description') }}
          </p>
          <AppButton block @click="handleGoToHousehold">
            {{ t('magic_link.go_to_household') }}
          </AppButton>
        </div>

        <!-- Error State -->
        <div v-else-if="result && !result.success && !result.requiresAuth" class="p-8 text-center">
          <div class="w-14 h-14 flex items-center justify-center mx-auto mb-4" style="background: var(--ruby-100); border-radius: 6px;">
            <svg class="w-7 h-7 text-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 class="mb-2" style="font-size: var(--text-xl); color: var(--text-primary);">
            {{ result.errorTitle }}
          </h2>
          <p class="mb-6" style="color: var(--text-secondary); font-size: var(--text-sm);">
            {{ result.errorMessage }}
          </p>
          <AppButton variant="ghost" block @click="handleClose">
            {{ t('common.close') }}
          </AppButton>
        </div>

        <!-- Auth Required State -->
        <div v-else-if="result?.requiresAuth" class="p-8 text-center">
          <div class="w-14 h-14 flex items-center justify-center mx-auto mb-4" style="background: var(--steel-100); border-radius: 6px;">
            <svg class="w-7 h-7 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 class="mb-2" style="font-size: var(--text-xl); color: var(--text-primary);">{{ t('magic_link.auth_required_title') }}</h2>
          <p class="mb-2" style="color: var(--text-secondary); font-size: var(--text-sm);">
            {{ t('magic_link.auth_required_invited', { name: result.householdName }) }}
          </p>
          <p class="mb-6" style="color: var(--text-secondary); font-size: var(--text-sm);">
            {{ t('magic_link.auth_required_hint', { email: result.invitedEmail }) }}
          </p>
          <AppButton block class="mb-3" @click="handleSignIn">
            {{ t('magic_link.sign_in_cta') }}
          </AppButton>
          <AppButton variant="ghost" block @click="handleClose">
            {{ t('common.cancel') }}
          </AppButton>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import {
  acceptInvitationWithToken,
  clearMagicLinkParams,
  getMagicLinkParams,
  isMagicLinkUrl,
} from "../services/magicLinkService"
import { currentUser } from "../stores/authStore"
import { AppButton, AppSpinner } from "./ui"

const emit = defineEmits<{
  (e: "navigate", tab: string): void
  (e: "showAuth"): void
}>()

const { t } = useI18n()

interface AcceptResult {
  success: boolean
  requiresAuth?: boolean
  invitedEmail?: string
  householdName?: string
  errorTitle?: string
  errorMessage?: string
}

const showModal = ref(false)
const isLoading = ref(false)
const result = ref<AcceptResult | null>(null)
const pendingParams = ref<{ token: string; id: string } | null>(null)

async function processInvitation(token: string, invitationId: string) {
  isLoading.value = true
  showModal.value = true
  result.value = null

  try {
    const user = currentUser.value
    result.value = await acceptInvitationWithToken(token, invitationId, user)

    // If auth is required, store params and wait for user to sign in
    if (result.value.requiresAuth) {
      pendingParams.value = { token, id: invitationId }
    }
  } catch (error) {
    result.value = {
      success: false,
      errorTitle: t("magic_link.error_title"),
      errorMessage: t("magic_link.error_message"),
    }
  } finally {
    isLoading.value = false
  }
}

function handleClose() {
  showModal.value = false
  result.value = null
  pendingParams.value = null
  clearMagicLinkParams()
}

function handleGoToHousehold() {
  handleClose()
  emit("navigate", "household")
}

function handleSignIn() {
  emit("showAuth")
}

// Watch for user sign in after showing auth required
watch(currentUser, async (newUser) => {
  if (newUser && pendingParams.value && result.value?.requiresAuth) {
    // User signed in, retry the invitation acceptance
    await processInvitation(pendingParams.value.token, pendingParams.value.id)
  }
})

onMounted(() => {
  // Check if this is a magic link URL
  if (isMagicLinkUrl()) {
    const params = getMagicLinkParams()
    if (params) {
      processInvitation(params.token, params.id)
    }
  }
})
</script>
